<template>
  <div class="bg-white rounded-lg pb-5 px-6">
    <!-- Header with count -->
    <div class="flex items-center gap-3 mb-6">
      <div class="w-3 h-3 bg-green-500 rounded-full"></div>
      <h3 class="text-lg font-semibold text-gray-900">
        Confirmed Sessions ({{ activeSessions.length }})
      </h3>
    </div>

    <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 overflow-visible">
      <div
        v-for="session in paginatedSessions"
        :key="session.id"
        @click="openSessionDetails(session)"
        :class="[
          'bg-white rounded-lg p-4 sm:p-6 hover:scale-102 transition-all duration-300 overflow-hidden cursor-pointer',
          session.status === 'in-progress' 
            ? 'session-card-in-progress' 
            : 'border-2 border-green-800 shadow-[4px_4px_0_0_rgba(21,128,61,1)]'
        ]"
      >
        <!-- Session Header -->
        <div class="mb-4">
          <!-- Desktop: Student Info + Status Tags in same row -->
          <div class="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
            <!-- Student Info -->
            <div class="flex items-center gap-3">
              <!-- Show UserAvatar only if student is not deactivated -->
              <UserAvatar
                v-if="session.studentName !== 'Deactivated User'"
                :src="session.studentProfileImage"
                :name="session.studentName"
                size="md"
                class="flex-shrink-0"
              />
              <!-- Show placeholder for deactivated users -->
              <div
                v-else
                class="w-10 h-10 bg-gray-300 rounded-full flex items-center justify-center flex-shrink-0"
              >
                <svg class="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <div class="flex-1">
                <h4 class="font-medium text-gray-900">{{ session.studentName }}</h4>
              </div>
            </div>
            
            <!-- Status Tags - Desktop: upper right, Mobile: below -->
            <div class="flex items-center gap-2 flex-shrink-0">
              <span
                v-if="session.status === 'in-progress'"
                class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium text-white border-2 border-blue-800 shadow-lg animated-gradient whitespace-nowrap"
              >
                In Progress
              </span>
              <span
                v-else
                class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 border-2 border-green-800 whitespace-nowrap"
              >
                Confirmed
              </span>
              <span
                class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap"
                :class="getTypeClasses(session.type)"
              >
                {{ session.type === "face-to-face" ? "Face-to-Face" : "Online" }}
              </span>
            </div>
          </div>
        </div>

        <!-- Session Details -->
        <div class="space-y-3 mb-4">
          <div
            class="flex justify-between border-2 border-blue-800 bg-blue-50/50 rounded-lg py-2 px-4"
          >
            <div class="flex items-center gap-2 text-sm text-blue-800">
              <CalendarIcon class="w-4 h-4" />
              <span class="font-semibold">{{ session.date }}</span>
            </div>

            <div class="flex items-center gap-2 text-sm text-blue-800">
              <ClockIcon class="w-4 h-4" />
              <span class="font-semibold">{{ session.time }}</span>
            </div>
          </div>

          <div class="flex items-center gap-2 text-sm text-gray-800 px-4">
            <BookOpenIcon class="w-4 h-4" />
            <span class="font-semibold">{{ session.subject }}</span>
          </div>

          <div
            v-if="session.type === 'online'"
            class="border border-blue-200 bg-blue-50/50 rounded-sm py-2 px-4"
          >
            <div class="flex items-center gap-2 text-sm text-blue-700 mb-2">
              <VideoCameraIcon class="w-4 h-4" />
              <span class="font-semibold">Meeting Link:</span>
            </div>
            <!-- Show meeting link if within 30 minutes or in-progress -->
            <div
              v-if="shouldShowMeetingLink(session) && session.meetingLink && session.meetingLink !== 'To be provided'"
              class="flex items-center gap-2"
            >
              <a
                :href="session.meetingLink"
                target="_blank"
                rel="noopener noreferrer"
                class="flex-1 text-sm text-blue-600 hover:text-blue-800 underline truncate"
                :title="session.meetingLink"
              >
                {{ session.meetingLink }}
              </a>
              <button
                @click.stop="copyMeetingLink(session.meetingLink)"
                class="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-100 rounded transition-colors cursor-pointer"
                title="Copy link"
              >
                <DocumentDuplicateIcon class="w-4 h-4" />
              </button>
            </div>
            <!-- Show waiting message if not yet time -->
            <div v-else class="text-sm text-gray-600">
              <span v-if="session.status === 'confirmed'">
                Meeting link will be available 30 minutes before the session.
              </span>
              <span v-else>
                The meeting link will be generated once approved.
              </span>
            </div>
          </div>

          <div
            v-if="session.location"
            class="flex items-center border border-orange-200 bg-orange-50/50 rounded-sm py-2 px-4 gap-2 text-sm text-orange-700 min-w-0"
          >
            <MapPinIcon class="w-4 h-4 flex-shrink-0" />
            <span class="truncate" :title="session.location">{{ session.location }}</span>
          </div>

          <div
            v-if="session.notes"
            class="text-sm text-gray-600 border border-gray-200 bg-gray-50/50 rounded-sm py-2 px-4 break-words"
          >
            <span class="font-medium">Notes:</span> {{ truncateNotes(session.notes, 100) }}
          </div>

          <!-- Reschedule Status -->
        </div>

        <!-- Action Buttons -->
        <div class="flex gap-2 relative" @click.stop>
          <!-- Reschedule Button (only show if NOT in-progress) -->
          <div v-if="session.status !== 'in-progress'" class="flex-1">
            <button
              @click="handleRescheduleClick(session)"
              class="w-full px-3 py-2 bg-white border-2 border-orange-800 text-orange-800 rounded-md shadow-[4px_4px_0_0_rgba(194,65,12,1)] hover:bg-orange-500 hover:text-white transition-colors text-sm font-medium flex items-center justify-center gap-2 cursor-pointer"
              :title="getRescheduleButtonInfo(session).tooltip"
            >
              <ArrowPathIcon class="w-4 h-4" />
              {{ getRescheduleButtonInfo(session).text }}
            </button>
          </div>
          
          <!-- Mark as Complete Button (only show if in-progress) -->
          <div v-if="session.status === 'in-progress'" class="flex-1">
            <button
              @click="handleCompleteClick(session)"
              class="w-full px-3 py-2 bg-white border-2 border-green-800 text-green-800 rounded-md shadow-[4px_4px_0_0_rgba(21,128,61,1)] transition-colors text-sm font-medium flex items-center justify-center gap-2"
              :class="{ 
                'opacity-50 cursor-not-allowed': !canComplete(session),
                'hover:bg-green-500 hover:text-white cursor-pointer': canComplete(session)
              }"
              :disabled="!canComplete(session)"
              :title="getCompleteButtonInfo(session).tooltip"
            >
              <CheckCircleIcon class="w-4 h-4" />
              {{ getCompleteButtonInfo(session).text }}
            </button>
          </div>
          
          <!-- Cancel Button -->
          <div class="flex-1">
            <button
              @click="handleCancelClick(session)"
              class="w-full px-3 py-2 bg-white border-2 border-red-800 text-red-800 rounded-md shadow-[4px_4px_0_0_rgba(153,27,27,1)] hover:bg-red-500 hover:text-white transition-colors text-sm font-medium flex items-center justify-center gap-2 cursor-pointer"
              :title="getCancelButtonInfo(session).tooltip"
            >
              <XCircleIcon class="w-4 h-4" />
              {{ getCancelButtonInfo(session).text }}
            </button>
          </div>
        </div>

        <!-- Message Button -->
        <div class="mt-3">
          <button
          @click.stop="handleMessageStudent(session)"
            class="w-full px-3 py-2 bg-white border-2 border-blue-800 text-blue-800 rounded-md shadow-[4px_4px_0_0_rgba(30,64,175,1)] hover:bg-blue-500 hover:text-white transition-colors text-sm font-medium flex items-center justify-center gap-2 cursor-pointer"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
              />
            </svg>
            Message
          </button>
        </div>
      </div>
    </div>

    <!-- Pagination -->
    <div v-if="activeSessions.length > 0 && totalPages > 1" class="bg-white rounded-lg mt-4 pb-3 sm:pb-5 ">
      <div class="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0">
        <div class="text-xs sm:text-sm text-gray-600 order-2 sm:order-1">
          Showing {{ startIndex + 1 }}-{{ endIndex }} of {{ activeSessions.length }} confirmed sessions
        </div>
        <div class="flex items-center space-x-1 overflow-x-auto w-full sm:w-auto order-1 sm:order-2">
          <button
            @click="previousPage"
            :disabled="currentPage === 1"
            class="border-2 border-blue-800 rounded-md px-2 sm:px-3 py-1 text-xs sm:text-sm text-blue-700 hover:bg-blue-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors flex items-center gap-1 flex-shrink-0"
            title="Previous page"
          >
            <ChevronLeftIcon class="w-3 h-3 sm:w-4 sm:h-4" />
            <span class="hidden sm:inline">Previous</span>
            <span class="sm:hidden">Prev</span>
          </button>
          <button
            v-for="page in totalPages"
            :key="page"
            @click="goToPage(page)"
            :class="[
              'px-2 sm:px-3 py-1 rounded-md border-2 border-blue-800 transition-colors min-w-[32px] sm:min-w-[40px] text-xs sm:text-sm flex-shrink-0',
              page === currentPage
                ? 'bg-blue-600 text-white font-semibold'
                : 'text-blue-700 hover:bg-blue-50',
            ]"
          >
            {{ page }}
          </button>
          <button
            @click="nextPage"
            :disabled="currentPage === totalPages"
            class="border-2 border-blue-800 rounded-md px-2 sm:px-3 py-1 text-xs sm:text-sm text-blue-700 hover:bg-blue-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors flex items-center gap-1 flex-shrink-0"
            title="Next page"
          >
            <span class="hidden sm:inline">Next</span>
            <span class="sm:hidden">Next</span>
            <ChevronRightIcon class="w-3 h-3 sm:w-4 sm:h-4" />
          </button>
        </div>
      </div>
    </div>

    <!-- Reschedule Modal -->
    <SessionRescheduleModal
      :open="showRescheduleModal"
      :session="selectedSession ? {
        id: selectedSession.id,
        tutorId: selectedSession.tutorId || '',
        tutorName: selectedSession.studentName, 
        studentName: selectedSession.studentName,
        status: selectedSession.status === 'in-progress' ? 'confirmed' : selectedSession.status as 'confirmed' | 'pending' | 'completed' | 'cancelled',
        mode: selectedSession.type === 'face-to-face' ? 'face-to-face' : 'online',
        date: selectedSession.date,
        time: selectedSession.time,
        subject: selectedSession.subject,
        notes: selectedSession.notes,
        location: selectedSession.location,
        studentId: selectedSession.studentId || 'tutor-view' 
      } : undefined"
      @close="closeRescheduleModal"
      @reschedule="handleRescheduleSession"
    />

    <!-- Cancel Modal -->
    <SessionCancelModal
      :open="showSessionCancelModal"
      :session="selectedSession ? {
        id: selectedSession.id,
        tutorId: selectedSession.tutorId || '',
        tutorName: selectedSession.studentName, // For tutor view, show student name
        status: selectedSession.status === 'in-progress' || selectedSession.status === 'completed' || selectedSession.status === 'cancelled' ? 'confirmed' : selectedSession.status as 'confirmed' | 'pending',
        mode: selectedSession.type === 'face-to-face' ? 'face-to-face' : 'online',
        date: selectedSession.date,
        time: selectedSession.time,
        subject: selectedSession.subject,
        notes: selectedSession.notes,
        location: selectedSession.location,
        startAt: selectedSession.startAt, // Pass startAt for no-show detection
        studentId: selectedSession.studentId || 'tutor-view' // Add studentId to indicate tutor view
      } : undefined"
      @close="closeSessionCancelModal"
      @cancel="handleSessionCancel"
    />

    <!-- Mark Complete Confirmation Modal -->
    <transition name="modal-backdrop" appear>
      <div v-if="showCompleteModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999]" @click="closeCompleteModal">
        <transition name="modal" appear>
          <div @click.stop class="bg-white border-2 border-black shadow-[6px_6px_0_0_rgba(0,0,0,1)] rounded-lg p-6 w-full max-w-md mx-4">
            <!-- Header -->
            <div class="flex items-center justify-between mb-4">
              <h3 class="text-xl font-bold text-gray-900">Mark Session as Complete?</h3>
              <button @click="closeCompleteModal" class="text-gray-400 hover:text-gray-600 transition-colors">
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <!-- Content -->
            <div class="mb-6">
              <div class="flex items-center gap-3 mb-4 p-4 bg-green-50 rounded-lg border-2 border-green-200">
                <CheckCircleIcon class="w-8 h-8 text-green-600 flex-shrink-0" />
                <div>
                  <p class="text-sm text-gray-700">
                    Are you sure you want to mark this session with 
                    <span class="font-semibold text-gray-900">{{ selectedSession?.studentName }}</span> 
                    as completed?
                  </p>
                </div>
              </div>
              <p class="text-xs text-gray-500">This action cannot be undone.</p>
            </div>

            <!-- Buttons -->
            <div class="flex gap-3">
              <button
                @click="closeCompleteModal"
                :disabled="isProcessing"
                class="flex-1 px-4 py-2 bg-white border-2 border-gray-800 text-gray-800 rounded-md shadow-[2px_2px_0_0_rgba(0,0,0,1)] hover:bg-gray-100 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                @click="confirmComplete"
                :disabled="isProcessing"
                class="flex-1 px-4 py-2 bg-green-600 border-2 border-green-800 text-white rounded-md shadow-[2px_2px_0_0_rgba(0,0,0,1)] hover:bg-green-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <span v-if="!isProcessing">Yes, Mark Complete</span>
                <span v-else class="flex items-center gap-2">
                  <svg class="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing...
                </span>
              </button>
            </div>
          </div>
        </transition>
      </div>
    </transition>

    <!-- Success/Error Notification -->
    <SessionActionNotification
      :show="showNotification"
      :type="notificationType"
      :title="notificationTitle"
      :message="notificationMessage"
      @close="closeNotification"
    />

    <!-- Session Details Modal -->
    <SessionDetailsModal
      :isOpen="isModalOpen"
      :session="modalSelectedSession"
      @close="closeModal"
      @navigateToFeedback="() => {}"
      @openMessages="() => {}"
    />
    
    <!-- Session Toast Container -->
    <SessionToast />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from "vue";
import {
  CalendarIcon,
  ClockIcon,
  BookOpenIcon,
  VideoCameraIcon,
  MapPinIcon,
  ArrowPathIcon,
  XCircleIcon,
  DocumentDuplicateIcon,
  CheckCircleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/vue/24/outline";
import SessionRescheduleModal from "@/components/Student-Tutor/SessionRescheduleModal.vue";
import SessionCancelModal from "@/components/Student-Tutor/SessionCancelModal.vue";
import SessionActionNotification from "@/components/Student-Tutor/SessionActionNotification.vue";
import SessionDetailsModal from "@/components/Student-Tutor/SessionDetailsModal.vue";
import SessionToast from "@/components/Student-Tutor/SessionToast.vue";
import { canCancelSession, canRescheduleSession, canCompleteSession, isSessionWithin12Hours, getSessionTimeMessage } from "@/utils/sessionTimeUtils";
import { useToast } from "@/composables/useSessionToast";
import { useTutorSessionStore } from "@/composables/useTutorSessionStore";
import { auth } from "@/firebase";
import { sessionService } from "@/services/sessionService";
import { logDebug, logError, logInfo, logSuccess } from "@/utils/secureLogger";
import { useRouter } from "vue-router";
import UserAvatar from "@/components/UserAvatar.vue";

interface TutorSession {
  id: string;
  studentName: string;
  studentProfileImage?: string | null;
  status: "pending" | "confirmed" | "in-progress" | "completed" | "cancelled" | "declined";
  type: "face-to-face" | "online";
  date: string;
  time: string;
  subject: string;
  notes: string;
  location?: string;
  meetingLink?: string;
  startAt?: any;
  tutorId?: string;
  studentId?: string;
  materials?: Array<{
    name: string;
    url: string;
    size: number;
    type: string;
    uploadedAt?: Date;
    uploadedBy?: string;
  }>;
}

interface Props {
  sessions: TutorSession[];
}

const props = defineProps<Props>();

const emit = defineEmits<{
  reschedule: [session: TutorSession & { newDate?: string; newTime?: string }];
  cancel: [session: TutorSession];
}>();

// Use tutor session store
const { updateSessionStatus, deleteSessionCalendarEvent, removeFromBlockedSlots } =
  useTutorSessionStore();

// Router for navigation
const router = useRouter();
const { showWarning, showInfo } = useToast();

// Track which sessions have shown the meeting link notification
const notifiedSessions = ref<Set<string>>(new Set());

// Modal state
const showRescheduleModal = ref(false);
const showSessionCancelModal = ref(false);
const showCompleteModal = ref(false);
const selectedSession = ref<TutorSession | null>(null);
const isProcessing = ref(false);
const showTooltip = ref('');

// Modal state for session details
const isModalOpen = ref(false);
const modalSelectedSession = ref<any>(null);

// Notification state
const showNotification = ref(false);
const notificationType = ref<'success' | 'error'>('success');
const notificationTitle = ref('');
const notificationMessage = ref('');

// Pagination state
const currentPage = ref(1);
const itemsPerPage = 4;

// Filter out declined sessions (they should only appear in session history)
const activeSessions = computed(() => {
  return props.sessions.filter(session => 
    session.status !== 'declined' && 
    session.status !== 'cancelled' && 
    session.status !== 'completed'
  );
});

// Pagination calculations
const totalPages = computed(() => Math.ceil(activeSessions.value.length / itemsPerPage));
const startIndex = computed(() => (currentPage.value - 1) * itemsPerPage);
const endIndex = computed(() =>
  Math.min(startIndex.value + itemsPerPage, activeSessions.value.length)
);

// Get paginated sessions for current page
const paginatedSessions = computed(() => {
  return activeSessions.value.slice(startIndex.value, endIndex.value);
});

// Pagination methods
const goToPage = (page: number) => {
  currentPage.value = page;
};

const nextPage = () => {
  if (currentPage.value < totalPages.value) {
    currentPage.value++;
  }
};

const previousPage = () => {
  if (currentPage.value > 1) {
    currentPage.value--;
  }
};

// Timer to force re-evaluation of button states every minute
const currentTime = ref(new Date());
let timeUpdateInterval: ReturnType<typeof setInterval> | null = null;

// Set up timer on component mount
onMounted(() => {
  // Update current time every minute to trigger re-computation
  timeUpdateInterval = setInterval(() => {
    currentTime.value = new Date();
  }, 60000); // 60 seconds
});

// Clean up timer on component unmount
onUnmounted(() => {
  if (timeUpdateInterval) {
    clearInterval(timeUpdateInterval);
  }
});

// Close complete modal
const closeCompleteModal = () => {
  showCompleteModal.value = false;
  selectedSession.value = null;
};

// Confirm complete - execute session completion
const confirmComplete = async () => {
  if (!selectedSession.value || !auth.currentUser) return;
  
  try {
    isProcessing.value = true;
    const success = await updateSessionStatus(selectedSession.value.id, 'completed');
    
    if (success) {
      // Clean up blocked slots when session is completed
      const tutorId = selectedSession.value.tutorId || auth.currentUser.uid;
      await removeFromBlockedSlots(tutorId, selectedSession.value.id);
      logDebug('Blocked slot cleaned up for completed session', { 
        sessionId: selectedSession.value.id,
        tutorId 
      });
      
      notificationType.value = 'success';
      notificationTitle.value = 'Session Completed!';
      notificationMessage.value = `Session with ${selectedSession.value.studentName} has been marked as completed.`;
      showNotification.value = true;
      logSuccess('Session marked as completed', { sessionId: selectedSession.value.id });
      closeCompleteModal();
    } else {
      throw new Error('Failed to update session status');
    }
  } catch (error) {
    notificationType.value = 'error';
    notificationTitle.value = 'Error';
    notificationMessage.value = 'Failed to mark session as complete. Please try again.';
    showNotification.value = true;
    logError('Error marking session as complete', error instanceof Error ? error : new Error(String(error)));
  } finally {
    isProcessing.value = false;
  }
};

// Computed properties for time restrictions
const selectedSessionCanCancel = computed(() => {
  if (!selectedSession.value) return false;
  const sessionStartTime = parseSessionDateTime(selectedSession.value);
  const { canCancel } = canCancelSession(sessionStartTime, selectedSession.value.status);
  return canCancel;
});

const timeRestrictionMessage = computed(() => {
  if (!selectedSession.value) return "";
  const sessionStartTime = parseSessionDateTime(selectedSession.value);
  const { timeRestrictionMessage } = canCancelSession(
    sessionStartTime,
    selectedSession.value.status
  );
  return timeRestrictionMessage;
});

// Helper function to parse session date/time properly
const parseSessionDateTime = (session: TutorSession): Date => {
  if (session.startAt) {
    // Check if it's a Firestore Timestamp with toDate() method
    if (session.startAt.toDate && typeof session.startAt.toDate === 'function') {
      return session.startAt.toDate();
    }
    // Otherwise try to parse it as a regular date
    const date = new Date(session.startAt);
    if (!isNaN(date.getTime())) {
      return date;
    }
  }
  
  // Extract start time from time range (e.g., "2:00 PM - 4:00 PM" -> "2:00 PM")
  const startTime = session.time.split(' - ')[0].trim();
  
  // Create a proper date string
  const dateTimeString = `${session.date} ${startTime}`;
  const constructedDate = new Date(dateTimeString);
  
  // If date is still invalid, try alternative parsing
  if (isNaN(constructedDate.getTime())) {
    // Try ISO format construction
    const dateParts = session.date.split(', ');
    if (dateParts.length === 2) {
      const [dayName, dateStr] = dateParts;
      const isoDateString = `${dateStr} ${startTime}`;
      const alternativeDate = new Date(isoDateString);
      
      if (!isNaN(alternativeDate.getTime())) {
        logDebug('Date parsing successful using alternative method');
        return alternativeDate;
      }
    }
    
    // If all else fails, return a date far in the future to avoid disabling buttons incorrectly
    logError('Failed to parse session date, using fallback', new Error('Date parsing failed'));
    
    // Return a date 24 hours from now as fallback (allows actions)
    return new Date(Date.now() + 24 * 60 * 60 * 1000);
  }
  
  // Debug logging for successful parsing
  logDebug('Date parsing successful using standard method');
  
  return constructedDate;
};

// Time restriction checks (reactive to currentTime updates)
const canCancel = (session: TutorSession) => {
  // Reference currentTime to trigger re-computation
  const _now = currentTime.value;
  const sessionStartTime = parseSessionDateTime(session);
  const { canCancel } = canCancelSession(sessionStartTime, session.status);
  return canCancel;
};

const canReschedule = (session: TutorSession) => {
  // Reference currentTime to trigger re-computation
  const _now = currentTime.value;
  const sessionStartTime = parseSessionDateTime(session);
  const { canReschedule } = canRescheduleSession(sessionStartTime, session.status);
  return canReschedule;
};

const canComplete = (session: TutorSession) => {
  // Reference currentTime to trigger re-computation
  const _now = currentTime.value;
  const sessionStartTime = parseSessionDateTime(session);
  const { canComplete } = canCompleteSession(sessionStartTime, session.status);
  return canComplete;
};

// Enhanced reschedule checking with limit validation
const canRescheduleWithLimits = (session: TutorSession) => {
  // Reference currentTime to trigger re-computation
  const _now = currentTime.value;
  const sessionStartTime = parseSessionDateTime(session);
  
  // First check time restrictions
  const timeCheck = canRescheduleSession(sessionStartTime, session.status);
  
  if (!timeCheck.canReschedule) {
    return {
      canReschedule: false,
      reason: timeCheck.timeRestrictionMessage
    };
  }
  
  // Then check additional session-level restrictions (status, etc.)
  const sessionData = {
    ...session,
    sessionStatus: session.status,
    startAt: { toDate: () => sessionStartTime },
  };
  
  return sessionService.canRescheduleSession(sessionData as any, 'tutor');
};

// Enhanced cancel checking with time validation
const canCancelWithRestrictions = (session: TutorSession) => {
  // Reference currentTime to trigger re-computation
  const _now = currentTime.value;
  const sessionStartTime = parseSessionDateTime(session);
  return canCancelSession(sessionStartTime, session.status);
};

// Helper function to get reschedule button info for tutors
const getRescheduleButtonInfo = (session: TutorSession) => {
  // Reference currentTime to trigger re-computation
  const _now = currentTime.value;
  const rescheduleStatus = canRescheduleWithLimits(session);
  
  logDebug('Checking tutor reschedule eligibility');
  
  if (!rescheduleStatus.canReschedule) {
    return {
      disabled: true,
      text: "Reschedule",
      tooltip: rescheduleStatus.reason || "Reschedule not available"
    };
  }
  
  return {
    disabled: false,
    text: "Reschedule",
    tooltip: "Reschedule this session"
  };
};

// Helper function to get cancel button info for tutors
const getCancelButtonInfo = (session: TutorSession) => {
  // Reference currentTime to trigger re-computation
  const _now = currentTime.value;
  const cancelStatus = canCancelWithRestrictions(session);
  
  logDebug('Checking tutor cancel eligibility');
  
  if (!cancelStatus.canCancel) {
    return {
      disabled: true,
      text: "Cancel",
      tooltip: cancelStatus.timeRestrictionMessage
    };
  }
  
  return {
    disabled: false,
    text: "Cancel",
    tooltip: "Cancel this session"
  };
};

// Helper function to get complete button info for tutors
const getCompleteButtonInfo = (session: TutorSession) => {
  // Reference currentTime to trigger re-computation
  const _now = currentTime.value;
  const sessionStartTime = parseSessionDateTime(session);
  const { canComplete, timeRestrictionMessage } = canCompleteSession(sessionStartTime, session.status);
  
  logDebug('Checking tutor complete eligibility', { canComplete, timeRestrictionMessage });
  
  if (!canComplete) {
    return {
      disabled: true,
      text: "Mark Complete",
      tooltip: timeRestrictionMessage
    };
  }
  
  return {
    disabled: false,
    text: "Mark Complete",
    tooltip: "Mark this session as completed"
  };
};

// Check if meeting link should be visible (30 minutes before or during session)
const shouldShowMeetingLink = (session: TutorSession): boolean => {
  if (session.status === 'in-progress') return true;
  
  const sessionStartTime = parseSessionDateTime(session);
  const now = new Date();
  const minutesUntilSession = (sessionStartTime.getTime() - now.getTime()) / (1000 * 60);
  
  // Show link if within 30 minutes of start time
  const shouldShow = minutesUntilSession <= 30 && minutesUntilSession > 0;
  
  return shouldShow;
};

// Copy meeting link function
const copyMeetingLink = async (meetingLink: string) => {
  try {
    await navigator.clipboard.writeText(meetingLink);
  } catch (err) {
    logError('Failed to copy meeting link', err instanceof Error ? err : new Error(String(err)));
    // Fallback for older browsers
    const textArea = document.createElement("textarea");
    textArea.value = meetingLink;
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand("copy");
    document.body.removeChild(textArea);
  }
};

// Truncate notes to a maximum length
const truncateNotes = (notes: string, maxLength: number = 100) => {
  if (!notes) return '';
  if (notes.length <= maxLength) return notes;
  return notes.substring(0, maxLength) + '...';
};

// Set up interval to check for meeting link availability
let checkInterval: ReturnType<typeof setInterval>;

onMounted(() => {
  // Initialize reactive time
  currentTime.value = new Date();
  
  // Set up time update interval
  const timeUpdateInterval = setInterval(() => {
    currentTime.value = new Date();
  }, 60000); // Update every minute
  
  // Check for meeting link availability
  checkInterval = setInterval(() => {
    activeSessions.value.forEach((session: TutorSession) => {
      shouldShowMeetingLink(session);
    });
  }, 60000); // 60 seconds
  
  onUnmounted(() => {
    clearInterval(timeUpdateInterval);
    if (checkInterval) {
      clearInterval(checkInterval);
    }
  });
});

// New handlers that show toast notifications for restricted actions
const handleRescheduleClick = (session: TutorSession) => {
  const sessionStartTime = parseSessionDateTime(session);
  const { isWithin12Hours, hoursRemaining } = isSessionWithin12Hours(sessionStartTime);
  
  if (isWithin12Hours) {
    showWarning(
      "Cannot Reschedule",
      getSessionTimeMessage(hoursRemaining)
    );
    return;
  }
  
  // If not within 4 hours, proceed with normal reschedule logic
  const rescheduleStatus = canRescheduleWithLimits(session);
  if (!rescheduleStatus.canReschedule) {
    showWarning(
      "Cannot Reschedule",
      rescheduleStatus.reason || "Reschedule not available"
    );
    return;
  }
  
  openRescheduleModal(session);
};

const handleCancelClick = (session: TutorSession) => {
  const sessionStartTime = parseSessionDateTime(session);
  
  // For in-progress sessions, check the 15-minute rule
  if (session.status === 'in-progress') {
    const { canCancel, timeRestrictionMessage } = canCancelSession(sessionStartTime, session.status);
    if (!canCancel) {
      showWarning("Cannot Cancel", timeRestrictionMessage);
      return;
    }
    openSessionCancelModal(session);
    return;
  }
  
  // For other sessions, check 12-hour rule
  const { isWithin12Hours, hoursRemaining } = isSessionWithin12Hours(sessionStartTime);
  
  if (isWithin12Hours) {
    showWarning(
      "Cannot Cancel",
      getSessionTimeMessage(hoursRemaining)
    );
    return;
  }
  
  // If not within 4 hours, proceed with normal cancel logic
  const cancelStatus = canCancelWithRestrictions(session);
  if (!cancelStatus.canCancel) {
    showWarning(
      "Cannot Cancel",
      cancelStatus.timeRestrictionMessage
    );
    return;
  }
  
  openSessionCancelModal(session);
};

const handleCompleteClick = async (session: TutorSession) => {
  const sessionStartTime = parseSessionDateTime(session);
  const { canComplete, timeRestrictionMessage } = canCompleteSession(sessionStartTime, session.status);
  
  if (!canComplete) {
    showWarning("Cannot Mark Complete", timeRestrictionMessage);
    return;
  }
  
  // Show custom complete modal
  selectedSession.value = session;
  showCompleteModal.value = true;
};

// Modal handlers
const openRescheduleModal = (session: TutorSession) => {
  selectedSession.value = session;
  showRescheduleModal.value = true;
};

const openSessionCancelModal = (session: TutorSession) => {
  selectedSession.value = session;
  showSessionCancelModal.value = true;
};

const closeRescheduleModal = () => {
  showRescheduleModal.value = false;
  selectedSession.value = null;
  isProcessing.value = false;
};

const closeSessionCancelModal = () => {
  showSessionCancelModal.value = false;
  selectedSession.value = null;
  isProcessing.value = false;
};

// Session details modal handlers
const openSessionDetails = (session: any) => {
  console.log('🔍 Tutor Session Details:', session);
  console.log('👤 Student Name:', session.studentName);
  console.log('👨‍🏫 Tutor Name:', session.tutorName);
  modalSelectedSession.value = session;
  isModalOpen.value = true;
};

const closeModal = () => {
  isModalOpen.value = false;
  modalSelectedSession.value = null;
};

// Message handler function
const handleMessageStudent = async (session: TutorSession) => {
  try {
    logInfo('Starting conversation from session', { sessionId: session.id });
    
    // Import the messaging permission service
    const { messagingPermissionService } = await import('@/services/messagingPermissionService');
    
    // Get current user
    const currentUser = auth.currentUser;
    if (!currentUser) {
      alert('Please log in to send messages');
      return;
    }

    // Create conversation from session context
    const result = await messagingPermissionService.createConversationFromSession(
      session.id,
      currentUser.uid
    );

    if (!result.success) {
      alert(result.reason || 'Unable to start conversation');
      return;
    }

    // Get the student's information for CometChat
    const studentId = session.studentId;
    const studentName = session.studentName;
    
    if (!studentId) {
      alert('Unable to identify the student for messaging');
      return;
    }
    
    // Note: CometChat user creation and initialization will be handled by the messaging view

    // Navigate to tutor messages view with the specific conversation
    router.push({
      path: '/tutor-messages',
      query: { 
        conversation: result.conversationId,
        contact: studentId
      }
    });
    
    logSuccess('Successfully navigated to messaging with student', { 
      studentId, 
      conversationId: result.conversationId 
    });
    
  } catch (error) {
    logError('Error starting conversation with student', error);
    alert('Failed to start conversation. Please try again.');
  }
};

// Handle session reschedule
const handleRescheduleSession = async (data: {
  sessionId: string;
  newDate: string;
  newTime: string;
  reason?: string;
}) => {
  if (!selectedSession.value) return;

  isProcessing.value = true;

  try {
    // Close modal and show success notification immediately for better UX
    closeRescheduleModal();
    
    // Show success notification optimistically
    notificationType.value = 'success';
    notificationTitle.value = 'Session Rescheduled!';
    notificationMessage.value = 'The session has been successfully rescheduled. The student will be notified of the change.';
    showNotification.value = true;

    // Call the session service to reschedule the session (continues in background)
    await sessionService.rescheduleSession(
      data.sessionId,
      data.newDate,
      data.newTime,
      data.reason
    );

    // Audit log is now handled centrally in sessionService.rescheduleSession()

    // Emit reschedule event to parent
    emit("reschedule", {
      ...selectedSession.value,
      newDate: data.newDate,
      newTime: data.newTime,
    });
  } catch (error) {
    logError('Error rescheduling session', error instanceof Error ? error : new Error(String(error)));
    
    // Show error notification
    notificationType.value = 'error';
    notificationTitle.value = 'Reschedule Failed';
    notificationMessage.value = error instanceof Error ? error.message : 'Failed to reschedule session. Please try again.';
    showNotification.value = true;
  } finally {
    isProcessing.value = false;
  }
};

// Handle session cancel
const handleSessionCancel = async (data: {
  sessionId: string;
  reason: string;
  cancelledBy: string;
  cancelledAt: Date;
}) => {
  if (!selectedSession.value || !auth.currentUser) {
    logError('No selected session or current user', new Error('Invalid state'));
    return;
  }

  isProcessing.value = true;

  try {
    // Store session data before it potentially becomes null
    const sessionData = { ...selectedSession.value };
    
    logInfo('Cancelling session');

    // Close modal and show success notification immediately for better UX
    closeSessionCancelModal();
    
    // Show success notification optimistically
    notificationType.value = 'success';
    notificationTitle.value = 'Session Cancelled!';
    notificationMessage.value = 'The session has been successfully cancelled. The student will be notified.';
    showNotification.value = true;

    // Use the proper session service which handles emails, calendar, and database updates (continues in background)
    await sessionService.cancelSession(
      data.sessionId,
      data.reason,
      data.cancelledBy,
      data.cancelledAt
    );

    logSuccess('Session cancelled successfully with email notification');

    // Emit cancel event to parent with the stored session data
    emit("cancel", sessionData);

  } catch (error) {
    logError('Error cancelling session', error instanceof Error ? error : new Error(String(error)));
    
    // If the main cancellation failed, try fallback approach
    try {
      logInfo('Attempting fallback cancellation');
      
      // Fallback: just update the session status without email/calendar
      const success = await updateSessionStatus(
        data.sessionId,
        "cancelled",
        data.reason,
        `Cancelled by tutor: ${data.cancelledBy}`
      );

      if (success) {
        logSuccess('Session status updated using fallback mode');
        emit("cancel", selectedSession.value);
      } else {
        logError('Fallback cancellation also failed', new Error('Update session status failed'));
      }
    } catch (fallbackError) {
      logError('Fallback cancellation error', fallbackError instanceof Error ? fallbackError : new Error(String(fallbackError)));
    }
  } finally {
    isProcessing.value = false;
  }
};

// Notification methods
const closeNotification = () => {
  showNotification.value = false;
};

const getTypeClasses = (type: string) => {
  switch (type) {
    case "face-to-face":
      return "bg-orange-100 text-orange-800 border-2 border-orange-800";
    case "online":
      return "bg-blue-100 text-blue-800 border-2 border-blue-800";
    default:
      return "bg-gray-100 text-gray-800 border-2 border-gray-800";
  }
};
</script>

<style scoped>
@keyframes tooltip-in {
  0% {
    opacity: 0;
    transform: translateX(-50%) translateY(4px);
  }
  100% {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
}

.animate-tooltip-in {
  animation: tooltip-in 0.4s ease-out forwards;
}

/* Pulsing blue effect for in-progress sessions */
.session-card-in-progress {
  border: 3px solid #1e40af;
  animation: pulse-shadow 2s ease-in-out infinite;
}

@keyframes pulse-shadow {
  0%, 100% {
    box-shadow: 
      4px 4px 0 0 rgba(30, 64, 175, 1),
      0 0 0 0 rgba(30, 64, 175, 0);
  }
  50% {
    box-shadow: 
      5px 5px 0 0 rgba(30, 64, 175, 1),
      0 0 20px 5px rgba(30, 64, 175, 0.3);
  }
}

/* Animated gradient for in-progress label */
.animated-gradient {
  background: linear-gradient(90deg, #146eff, #5d38ff, #6222eb);
  background-size: 300% 100%;
  animation: gradient-shift 3s ease infinite;
}

@keyframes gradient-shift {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 90% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}
</style>
